import type { Theme } from '@mui/material';
import { resolveTokens, type AppTokens } from '@/theme';
import { flattenLayer, labelContrast } from '@/theme/textLabel';

/** The v2 sidebar (visual contract v2 §3): 236 px, also the drawer's width at `md` and below. */
export const SIDEBAR_WIDTH = 236;

type SidebarRoles = Pick<
  AppTokens,
  'surface' | 'surface2' | 'focusRing' | 'link' | 'hover' | 'brand' | 'brandSoft' | 'text'
>;

/**
 * The sidebar's canvas (TMMAPP-0151 §A 5: the prototype's `surface2`). saas-auth guarantees on
 * `surface2` neither the 3:1 `focusRing` nor `link` under the `hover` layer (the open Money Tools
 * row), so the canvas is derived like `textLabel`: `surface2` where both measure on this brand's
 * mode, else `surface`, where both are guaranteed. Proved by construction and measured in
 * `sidebarPaper.test.ts`.
 */
export function sidebarCanvas(roles: SidebarRoles): 'surface' | 'surface2' {
  const focus = labelContrast(roles.focusRing, roles.surface2) >= 3;
  const linkUnderHover =
    labelContrast(roles.link, flattenLayer(roles.hover, roles.surface2)) >= 4.5;
  return focus && linkUnderHover ? 'surface2' : 'surface';
}

/**
 * The current Money Tools row's ink (§A 5: `brand` on `brandSoft`, 7.90:1 on the v2 brand): `brand`
 * where it measures 4.5:1 on `brandSoft`, else `text` (guaranteed on `brandSoft`).
 */
export function currentToolInk(roles: SidebarRoles): string {
  return labelContrast(roles.brand, roles.brandSoft) >= 4.5 ? roles.brand : roles.text;
}

/** The sidebar's paper — also `GatedShell`'s: the derived canvas, with a 1 px `line` rule at its end. */
export function sidebarPaperSx(theme: Theme) {
  const tokens = resolveTokens(theme);
  return {
    width: SIDEBAR_WIDTH,
    boxSizing: 'border-box',
    backgroundColor: tokens[sidebarCanvas(tokens)],
    borderInlineEnd: '1px solid',
    borderColor: tokens.line,
  } as const;
}
